import type React from 'react';
import { useState } from 'react';
import { Modal } from '../../core/Modal';
import styles from './ShaderPickerModal.module.css';

interface PickerItem {
  itemId: string;
  itemName: string;
  itemRender: React.ComponentType<any>;
  description?: string;
  tags?: string[];
  disabled?: boolean;
}

interface PickerCategory {
  categoryName: string;
  items: PickerItem[];
}

interface ShaderPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (itemId: string) => void;
  items: PickerCategory[];
  title?: string;
}

export const ShaderPickerModal: React.FC<ShaderPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  items,
  title = 'Choose an Item',
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const handleItemSelect = (item: PickerItem) => {
    if (item.disabled) return;
    onSelect(item.itemId);
    onClose();
  };

  const filteredCategories = selectedCategory
    ? items.filter((cat) => cat.categoryName === selectedCategory)
    : items;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className={styles.container}>
        {/* Category Filter */}
        <div className={styles.categoryFilter}>
          <button
            className={`${styles.categoryButton} ${
              !selectedCategory ? styles.active : ''
            }`}
            onClick={() => setSelectedCategory(null)}
            type="button"
          >
            All
          </button>
          {items.map((category) => (
            <button
              key={category.categoryName}
              className={`${styles.categoryButton} ${
                selectedCategory === category.categoryName ? styles.active : ''
              }`}
              onClick={() => setSelectedCategory(category.categoryName)}
              type="button"
            >
              {category.categoryName}
            </button>
          ))}
        </div>

        {/* Item Grid */}
        <div className={styles.itemGrid}>
          {filteredCategories.flatMap((category) =>
            category.items.map((item) => (
              <div
                key={item.itemId}
                className={`${styles.itemCard} ${
                  item.disabled ? styles.disabled : ''
                }`}
                onClick={() => handleItemSelect(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleItemSelect(item);
                  }
                }}
                role="button"
                tabIndex={item.disabled ? -1 : 0}
                aria-disabled={item.disabled}
              >
                <div className={styles.previewContainer}>
                  <item.itemRender />
                </div>
                <div className={styles.itemInfo}>
                  <h4 className={styles.itemName}>{item.itemName}</h4>
                  {item.description && (
                    <p className={styles.itemDescription}>{item.description}</p>
                  )}
                  {item.tags && item.tags.length > 0 && (
                    <div className={styles.tags}>
                      {item.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};
